/**
 * @name getSectionRefByVideoId
 * @description Maps a videoSectionId to its section ref.
 */
export const getSectionRefByVideoId = (videoSectionId, units) =>
	Object.keys(units).find((key) => units[key].videoSectionId === videoSectionId) || '';

/**
 * @name isSectionEligible
 * @description Checks if a section has no existing AB/multi split data.
 */
export const isSectionEligible = (sectionRef, units) =>
	!units[sectionRef].abSplitTestData && !units[sectionRef].multiSplitTestData;

/**
 * @name getEligibleSplitIds
 * @description Returns split IDs that have eligible sections.
 */
export const getEligibleSplitIds = (splits, units) =>
	Object.keys(splits).filter((id) => {
		const sectionRef = getSectionRefByVideoId(id, units);
		return sectionRef && isSectionEligible(sectionRef, units);
	});

/**
 * @name findHundredPercentSplit
 * @description Finds a 100% split if it exists.
 */
export const findHundredPercentSplit = (splits, eligibleIds) =>
	eligibleIds.find((id) => splits[id].splitPercentage === 100);

/**
 * @name getSortedPositiveSplits
 * @description Returns positive splits sorted by percentage descending.
 */
export const getSortedPositiveSplits = (splits, eligibleIds) =>
	eligibleIds
		.filter((id) => splits[id].splitPercentage > 0)
		.sort((a, b) => splits[b].splitPercentage - splits[a].splitPercentage);

/**
 * @name getDefaultControlSection
 * @description Gets the default live control section based on split configuration.
 * @param {object} sectionLevelSplit - The section level split config.
 * @param {object} units - The units configuration.
 * @returns {string} - The section ref of the live control section, or empty string.
 */
export const getDefaultControlSection = (sectionLevelSplit, units) => {
	if (!sectionLevelSplit?.enabled || !sectionLevelSplit?.splits) return '';

	const { splits } = sectionLevelSplit;
	const eligibleIds = getEligibleSplitIds(splits, units);

	const hundredPercentId = findHundredPercentSplit(splits, eligibleIds);
	if (hundredPercentId) return getSectionRefByVideoId(hundredPercentId, units);

	const [highestId] = getSortedPositiveSplits(splits, eligibleIds);
	return highestId ? getSectionRefByVideoId(highestId, units) : '';
};

export const getInitialExpSectionName = (detectedRef, unitsMap) => {
	if (detectedRef && unitsMap[detectedRef]) {
		return unitsMap[detectedRef].name;
	}
	return '';
};

/**
 * @name getMultiSplitIndex
 * @description Returns the index of a multi-split experiment section.
 */
export const getMultiSplitIndex = (unitData) => {
	return unitData?.multiSplitTestData?.index || '';
};

/**
 * @name getSectionNameToDisplay
 * @description Returns the display name for a section, appending index for multi-split experiments.
 */
export const getSectionNameToDisplay = (unitData) => {
	const index = getMultiSplitIndex(unitData);
	return index ? `${unitData.name}_${index}` : unitData.name;
};